<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
            <d-col col sm="6" class="text-left text-sm-left mb-4 mb-sm-0">
        <h3 class="page-title">
          <template v-if="isEdit">Edytuj składnik</template>
          <template v-else>Nowy składnik</template>
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="primary"
          outline
          @click.prevent="$router.push('/ingredients/')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          Wróć do listy
        </d-button>
        <d-button
          v-if="isEdit"
          theme="warning"
          @click.prevent="showDuplicateModal"
          class="mr-1">
          <i class="material-icons">content_copy</i>
          Duplikuj
        </d-button>
        <d-button
          theme="success"
          @click.prevent="validateForm">
          <i class="material-icons">done</i>
          Zapisz zmiany
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-row>
        <d-col :cols="(data.available_in_drink) ? 6 : 12">
          <d-card class="mb-5">
            <d-card-body>
              <validation-observer
                v-if="allDataLoaded"
                ref="ingredient-form"
                tag="form">
                <div class="form-group">
                  <label for="name">
                    Nazwa:
                  </label>

                  <validation-provider
                    rules="required"
                    name="name"
                    v-model="data.name"
                    v-slot="{ errors }">
                    <d-input
                      :state="errors.length ? 'invalid' : null"
                      v-model="data.name" />
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </div>

                <div class="form-group">
                  <label for="name">
                    Nazwa na wydruku:
                  </label>

                  <validation-provider
                    rules="required"
                    name="preview_name"
                    v-model="data.preview_name"
                    v-slot="{ errors }">
                    <d-input
                      :state="errors.length ? 'invalid' : null"
                      v-model="data.preview_name" />
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </div>

                <div class="form-group">
                  Składnik dostępny w:
                  <template v-if="!isEdit"> &nbsp; </template>
                  <d-button-group v-if="!isEdit">
                    <d-button
                      @click.prevent="setAsFoodIngredient()"
                      :outline="!data.available_in_food">
                      Posiłkach
                    </d-button>
                    <d-button
                      @click.prevent="setAsDrinkIngredient()"
                      :outline="!data.available_in_drink">
                      Napojach
                    </d-button>
                  </d-button-group>

                  <template v-if="isEdit">
                    <strong
                      v-if="data.available_in_food"
                      class="text-secondary">
                      Posiłkach
                    </strong>
                    <strong
                      v-else-if="data.available_in_drink"
                      class="text-secondary">
                      Napojach
                    </strong>
                  </template>
                </div>

                <div
                  v-if="data.available_in_food"
                  class="form-group">
                  Typ składnika:
                  <template v-if="!isEdit"> &nbsp; </template>
                  <d-button-group v-if="!isEdit">
                    <d-button
                      @click.prevent="setAsSimpleIngredient()"
                      :outline="isComplexIngredient">
                      Prosty
                    </d-button>
                    <d-button
                      @click.prevent="setAsComplexIngredient()"
                      :outline="!isComplexIngredient">
                      Złożony
                    </d-button>
                  </d-button-group>

                  <template v-if="isEdit">
                    <strong
                      v-if="!isComplexIngredient"
                      class="text-secondary">
                      Prosty
                    </strong>
                    <strong
                      v-else-if="isComplexIngredient"
                      class="text-secondary">
                      Złożony
                    </strong>
                  </template>
                </div>

                <div
                  v-if="data.available_in_food"
                  class="form-group">
                  Rozmiar składnika: &nbsp;
                  <d-button-group v-if="!isEdit">
                    <d-button
                      @click.prevent="setAsNormalIngredient"
                      :outline="data.is_big_ingredient">
                      Normalny
                    </d-button>
                    <d-button
                      @click.prevent="setAsBigIngredient"
                      :outline="!data.is_big_ingredient">
                      Duży
                    </d-button>
                  </d-button-group>

                  <template v-else>
                    <strong v-if="!data.is_big_ingredient" class="text-secondary">Normalny</strong>
                    <strong v-if="data.is_big_ingredient" class="text-secondary">Duży</strong>
                  </template>
                </div>

                <div
                  v-if="!isEdit && data.available_in_drink"
                  class="form-group">
                  <d-form-radio v-model="drinkIngredientType" value="fluid">Składnik płynny</d-form-radio>
                  <d-form-radio v-model="drinkIngredientType" value="cover">Wierzchni składnik dla napojów</d-form-radio>
                  <d-form-radio v-model="drinkIngredientType" value="addon">Składnik wrzucany do napoju</d-form-radio>
                  <d-form-radio v-model="drinkIngredientType" value="topping">Wierzchnia ozdoba dla napojów</d-form-radio>
                </div>

                <div
                  v-if="isEdit && data.available_in_drink"
                  class="form-group">
                  Rodzaj składnika:
                  <strong v-if="drinkIngredientType === 'fluid'" class="text-secondary">Składnik płynny</strong>
                  <strong v-if="drinkIngredientType === 'cover'" class="text-secondary">Wierzchni składnik dla napojów</strong>
                  <strong v-if="drinkIngredientType === 'addon'" class="text-secondary">Składnik wrzucany do napoju</strong>
                  <strong v-if="drinkIngredientType === 'topping'" class="text-secondary">Wierzchnia ozdoba dla napojów</strong>
                </div>

                <div
                  v-if="data.available_in_drink && ingredientIsFluid"
                  class="form-group color-picker-wrapper">
                  <label for="color">
                    Kolor składnika:
                  </label>

                  <swatches-picker
                    v-model="data.color"
                    @mounted="onSwatchesMounted"
                    ref="swatches" />
                </div>
              </validation-observer>

              <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center">
                Trwa ładowanie danych&hellip;
              </div>

              <d-alert
                v-if="loadError"
                show
                theme="warning">
                Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
              </d-alert>
            </d-card-body>
          </d-card>
        </d-col>

        <d-col
          v-if="data.available_in_drink && ingredientIsFluid"
          cols="6">
          <div
            v-if="ingredientIsFluid"
            class="form-group">
            <fluid
              :color="typeof data.color === 'object' ? data.color.hex : data.color"
              :height="300"
              :bottom="0"
              :maxHeight="300" />
          </div>
        </d-col>

        <d-col
          v-if="data.available_in_drink && !ingredientIsFluid"
          cols="6">
          <label for="image" class="w-100 text-center">
            Ilustracja<br>
            <small>Dodawana ilustracja powinna być plikiem SVG z przeźroczystością o maksymalnej wielkości 800x200px.</small>
          </label>
          <div class="text-center mt-2">
            <d-button
              v-if="data.image_url"
              theme="danger"
              outline
              @click.prevent="removeImage()">
              Zmień ilustrację
            </d-button>

            <file-input
              v-if="!data.image_url"
              :onChange="setImage"
              supportedFormats=".svg"
              id="image" />
          </div>

          <div
            v-if="isEdit && data.image_url"
            class="drink-image-wrapper d-flex">
            <img
              :src="data.image_url"
              class="my-4 mx-auto" />
          </div>
        </d-col>

        <d-col
          v-if="(data.available_in_food) && !isComplexIngredient"
          cols="6"
          class="mx-auto">
          <div class="form-group">
            <label for="image" class="w-100 text-center">
              Ilustracja<br>
              <small>Ilustracja powinna być plikiem SVG z przeźroczystością.</small>
            </label>

            <file-input
              v-if="!data.image_url"
              ref="file-input"
              :onChange="setSvgImage"
              supportedFormats=".svg"
              id="svgImage" />
          </div>
        </d-col>

        <d-col
          v-if="(data.available_in_food) && !isComplexIngredient"
          cols="12"
          class="mx-auto">
          <div class="form-group">
            <div
              v-if="svgCode"
              :class="{
                'image-wrapper': true,
                'is-big': !!data.is_big_ingredient
              }">
              <div
                class="image-wrapper-code"
                v-html="pureSvgCode">
              </div>
              <div
                ref="editor-editable-layer"
                @mouseup="editorEditableLayerMouseUp"
                @mousedown="editorEditableLayerMouseDown"
                @mousemove="editorEditableLayerMouseMove"
                @mouseleave="editorEditableLayerMouseUp"
                class="image-wrapper-editable-layer"></div>
              <div
                v-if="simpleEditorState.showGrid"
                class="image-area-lines-y">
                <div
                  v-for="(line, index) of new Array(!!data.is_big_ingredient ? 8 : 4)"
                  :key="'line-y-' + index"
                  class="image-area-line-y"></div>
              </div>

              <div
                v-if="simpleEditorState.showGrid"
                class="image-area-lines-x">
                <div
                  v-for="(line, index) of new Array(15)"
                  :key="'line-x-' + index"
                  class="image-area-line-x"></div>
              </div>
            </div>

            <p class="text-center">
              <toggle-switch
                v-if="svgCode"
                v-model="simpleEditorState.showGrid">
                Pokaż siatkę pomocniczą
              </toggle-switch>
            </p>

            <div
              v-if="svgCode"
              class="image-wrapper-scale mt-2 text-center">
              <span>Skaluj element (w %):</span><br>
              <d-input-group class="mt-2">
                <d-input-group-addon prepend>
                  <d-btn
                    outline
                    theme="danger"
                    @click.prevent="editorDecreaseLayerScale()"
                    :disabled="simpleEditorState.layerScaleInPercents <= 10">
                    <i class="material-icons">remove</i>
                  </d-btn>
                </d-input-group-addon>
                <d-input
                  class="text-center"
                  type="number"
                  min="0"
                  @input="editorUpdateLayerScale()"
                  v-model="simpleEditorState.layerScaleInPercents" />
                <d-input-group-addon append>
                  <d-btn
                    outline
                    theme="success"
                    @click.prevent="editorIncreaseLayerScale()">
                    <i class="material-icons">add</i>
                  </d-btn>
                </d-input-group-addon>
              </d-input-group>
            </div>

            <div
              v-if="isEdit && data.image_url"
              :class="{
                'image-wrapper': true,
                'is-big': data.is_big_ingredient
              }">
              <img :src="data.image_url" />
            </div>

            <p
              v-if="isEdit && data.image_url"
              class="text-center">
              <d-button
                theme="danger"
                outline
                @click.prevent="removeImage()">
                Zmień ilustrację
              </d-button>
            </p>
          </div>
        </d-col>

        <d-col
          v-if="data.available_in_food && isComplexIngredient"
          cols="12"
          class="mx-auto">
          <d-row>
            <d-col
              cols="4"
              class="complex-editor-sidebar">
              <d-btn
                theme="success"
                class="w-100"
                @click.prevent="addIngredientToComplexProduct()">
                Dodaj składnik
              </d-btn>

              <draggable
                v-if="complexEditorState.layers.length"
                v-model="complexEditorState.layers"
                tag="ol"
                item-key="id"
                class="layers-list mt-3">
                <template #item="{element: layer, index}">
                  <li
                    @click="toggleLayerSelection(layer.id)"
                    :class="{ 'layer': true, 'is-selected': complexEditorState.selectedLayer === layer.id }">
                    <d-card>
                      <d-card-body class="pt-3 pb-3 pl-3 pr-3">
                        <d-row class="layer-values">
                          <div
                            @click.stop
                            class="form-control is-select">
                            <v-select
                              :options="ingredientsList"
                              v-model="complexEditorState.layers[index].ingredientID"
                              :searchable="true"
                              :clearable="false"
                              :reduce="ingredient => ingredient.id"
                              label="name"
                              placeholder="Wybierz składnik"
                              @update:modelValue="loadIngredient(index)">
                            </v-select>
                          </div>
                        </d-row>
                      </d-card-body>

                      <a
                        href="#delete"
                        class="layer-remove text-white"
                        @click.stop.prevent="removeLayer(layer.id)">
                        <i class="material-icons">delete</i>
                      </a>
                    </d-card>
                  </li>
                </template>
              </draggable>

              <object
                ref="svg-image-loader"
                style="width: 1px; height: 1px; overflow: hidden; opacity: 0;"
                data=""
                @load="svgImageLoaded" />
            </d-col>

            <d-col cols="8">
              <div class="form-group">
                <div :class="{
                  'image-wrapper': true,
                  'mt-0': true,
                  'is-big': !!data.is_big_ingredient
                }">
                  <div
                    class="image-wrapper-code"
                    v-html="pureSvgCode">
                  </div>
                  <div
                    ref="editor-editable-layer"
                    @mouseup="editorEditableLayerMouseUp"
                    @mousedown="editorEditableLayerMouseDown"
                    @mousemove="editorEditableLayerMouseMove"
                    :hidden="complexEditorState.selectedLayer === false || (complexEditorSelectedLayerIndex !== false && !complexEditorState.layers[complexEditorSelectedLayerIndex].ingredientID)"
                    class="image-wrapper-editable-layer"></div>
                  <div
                    v-if="complexEditorState.showGrid"
                    class="image-area-lines-y">
                    <div
                      v-for="(line, index) of new Array(!!data.is_big_ingredient ? 8 : 4)"
                      :key="'line-y-' + index"
                      class="image-area-line-y"></div>
                  </div>

                  <div
                    v-if="complexEditorState.showGrid"
                    class="image-area-lines-x">
                    <div
                      v-for="(line, index) of new Array(15)"
                      :key="'line-x-' + index"
                      class="image-area-line-x"></div>
                  </div>
                </div>

                <p class="text-center">
                  <toggle-switch v-model="complexEditorState.showGrid">
                    Pokaż siatkę pomocniczą
                  </toggle-switch>
                </p>

                <div
                  v-if="complexEditorSelectedLayerIndex !== false && complexEditorState.layers[complexEditorSelectedLayerIndex].ingredientID"
                  class="image-wrapper-scale mt-2 text-center">
                  <span>Skaluj zaznaczony element (w %):</span><br>
                  <d-input-group class="mt-2">
                    <d-input-group-addon prepend>
                      <d-btn
                        outline
                        theme="danger"
                        @click.prevent="editorDecreaseLayerScale()"
                        :disabled="complexEditorState.layers[complexEditorSelectedLayerIndex].layerScaleInPercents <= 10">
                        <i class="material-icons">remove</i>
                      </d-btn>
                    </d-input-group-addon>
                    <d-input
                      class="text-center"
                      type="number"
                      min="0"
                      @input="editorUpdateLayerScale()"
                      v-model="complexEditorState.layers[complexEditorSelectedLayerIndex].layerScaleInPercents" />
                    <d-input-group-addon append>
                      <d-btn
                        outline
                        theme="success"
                        @click.prevent="editorIncreaseLayerScale()">
                        <i class="material-icons">add</i>
                      </d-btn>
                    </d-input-group-addon>
                  </d-input-group>
                </div>
              </div>
            </d-col>
          </d-row>
        </d-col>
      </d-row>
    </d-form>

    <object
      v-if="isEdit"
      ref="svg-image-type-checker"
      style="width: 1px; height: 1px; overflow: hidden; opacity: 0;"
      data=""
      @load="svgImageToCheckLoaded" />
  </d-container>
</template>

<script>
// import AppConfig from './../../.env';
import AppConfig from './../../config.js';
import DOMPurify from 'dompurify';
import { nextTick } from 'vue';
import Fluid from './Fluid.vue';
import FormUtils from './../../utils/FormUtils.js';
import SvgHelper from './../../utils/SvgHelper.js';
import vueDraggable from 'vuedraggable';
import ToggleSwitch from './../common/ToggleSwitch.vue';

export default {
  name: 'ingredient-form',
  components: {
    'fluid': Fluid,
    'draggable': vueDraggable,
    'toggle-switch': ToggleSwitch
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.unitsLoaded && this.ingredientsLoaded;
    },
    isEdit () {
      return !!this.$route.params.id;
    },
    unitsForRolls () {
      return this.units.filter(unit => unit.type === 'roll' && this.data.units.indexOf(unit.id) === -1);
    },
    unitsForUnits () {
      return this.units.filter(unit => unit.type === 'unit' && this.data.units.indexOf(unit.id) === -1);
    },
    ingredientIsFluid () {
      return this.data.available_in_drink && !this.data.is_addon && !this.data.is_cover && !this.data.is_topping;
    },
    ingredientsList () {
      if (this.data.available_in_food) {
        return this.ingredientsFood;
      }

      if (this.data.available_in_drink && this.data.is_addon) {
        return this.ingredientsAddons;
      }

      if (this.data.available_in_drink && this.data.is_cover) {
        return this.ingredientsCovers;
      }

      if (this.data.available_in_drink && this.data.is_topping) {
        return this.ingredientsToppings;
      }

      return [];
    },
    ingredientsAddons () {
      return this.ingredients.filter(ingredient => ingredient.available_in_drink && ingredient.is_addon);
    },
    ingredientsFood () {
      return this.ingredients.filter(ingredient => ingredient.available_in_food);
    },
    ingredientsCovers () {
      return this.ingredients.filter(ingredient => ingredient.available_in_drink && ingredient.is_cover);
    },
    ingredientsToppings () {
      return this.ingredients.filter(ingredient => ingredient.available_in_drink && ingredient.is_topping);
    },
    complexEditorSelectedLayerIndex () {
      for (let i = 0; i < this.complexEditorState.layers.length; i++) {
        if (this.complexEditorState.layers[i].id === this.complexEditorState.selectedLayer) {
          return i;
        }
      }

      return false;
    },
    wrappedSvgCode () {
      let svgHeight = this.data.is_big_ingredient ? 400 : 200;

      if (this.isComplexIngredient) {
        let layers = '';

        for (let i = this.complexEditorState.layers.length - 1; i >= 0; i--) {
          if (this.complexEditorState.layers[i].ingredientID) {
            let visibilityCssClass = '';

            if (
              this.complexEditorState.selectedLayer &&
              this.complexEditorState.selectedLayer !== this.complexEditorState.layers[i].id) {
              visibilityCssClass = 'class="is-inactive-layer-in-complex-editor"'
            }

            layers += `
              <g ${visibilityCssClass} data-layer="${i}" data-ingredient-id="${this.complexEditorState.layers[i].ingredientID}" data-layer-name="${this.complexEditorState.layers[i].ingredientName}" transform="translate(${this.complexEditorState.layers[i].layerX} ${this.complexEditorState.layers[i].layerY}) scale(${this.complexEditorState.layers[i].layerScale} ${this.complexEditorState.layers[i].layerScale})">
              ${this.complexEditorState.layers[i].svgCode}
              </g>
            `;
          }
        }

        if (layers === '') {
          return '';
        }

        return `<svg
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          width="800"
          height="${svgHeight}"
          viewBox="0 0 800 ${svgHeight}"
          AspectRatio="none"
          data-is-complex-product="true">
          ${layers}
        </svg>`;
      }

      return `<svg
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          width="800"
          height="${svgHeight}"
          viewBox="0 0 800 ${svgHeight}"
          AspectRatio="none">
          <g transform="translate(${this.simpleEditorState.layerX} ${this.simpleEditorState.layerY}) scale(${this.simpleEditorState.layerScale} ${this.simpleEditorState.layerScale})">
            ${this.svgCode}
          </g>
        </svg>`;
    },
    pureSvgCode () {
      return DOMPurify.sanitize(this.wrappedSvgCode);
    }
  },
  watch: {
    'drinkIngredientType': function (newValue, oldValue) {
      if (oldValue === null || oldValue === newValue) {
        return;
      }

      if (newValue === 'fluid') {
        this.data.image = null;
        this.data.is_cover = false;
        this.data.is_addon = false;
        this.data.is_topping = false;
        this.data.is_big_ingredient = false;
      } else if (newValue === 'cover') {
        this.data.is_cover = true;
        this.data.is_addon = false;
        this.data.is_topping = false;
        this.data.is_big_ingredient = false;
      } else if (newValue === 'addon') {
        this.data.is_cover = false;
        this.data.is_addon = true;
        this.data.is_topping = false;
        this.data.is_big_ingredient = false;
      } else if (newValue === 'topping') {
        this.data.is_cover = false;
        this.data.is_addon = false;
        this.data.is_topping = true;
        this.data.is_big_ingredient = false;
      }
    }
  },
  data() {
    return {
      data: {
        id: 0,
        name: '',
        available_in_food: true,
        available_in_drink: false,
        color: '#ffffff',
        is_cover: false,
        is_topping: false,
        is_addon: false,
        is_big_ingredient: false,
        preview_name: '',
        type: null,
        image: null,
        image_url: '',
        units: []
      },
      drinkIngredientType: 'fluid',
      isComplexIngredient: false,
      ingredients: [],
      units: [],
      loaded: false,
      loadError: false,
      ingredientsLoaded: false,
      unitsLoaded: false,
      svgCode: '',
      simpleEditorState: {
        layerX: 0,
        layerY: 0,
        layerScale: 2.5,
        imageWidth: 0,
        imageHeight: 0,
        layerScaleInPercents: 250,
        moveEditableLayer: false,
        clickedPointX: 0,
        clickedPointY: 0,
        showGrid: false
      },
      complexEditorState: {
        layers: [],
        moveEditableLayer: false,
        selectedLayer: false,
        clickedPointX: 0,
        clickedPointY: 0,
        showGrid: false
      }
    };
  },
  mounted () {
    if (this.isEdit) {
      this.loadItemData();
    } else {
      this.loaded = true;
    }

    this.loadAdditionalData();
  },
  methods: {
    setPresetColors () {
      console.log('Setting preset colors');
      const swatches = this.$refs.swatches;
      console.log('Swatches ref:', swatches);
      if (!swatches || !swatches.palette) {
        console.log('Swatches or palette not available');
        return;
      }
      
      let colors = [...swatches.palette];
      const lastRow = colors[colors.length - 1];
      
      if (lastRow && lastRow.length === 2) {
        colors[colors.length - 1] = [...lastRow, '#fef2dd'];
        swatches.palette = colors;
        console.log('Preset colors set');
      }
    },
    onSwatchesMounted() {
      console.log('Swatches component mounted');
      this.$nextTick(() => {
        this.setPresetColors();
      });
    },
    loadItemData () {
      FormUtils.loadItemData(this, {
        endpointBase: '/ingredients/item/',
        instanceFields: ['id', 'name', 'preview_name', 'type', 'image_url', 'units', 'available_in_food', 'available_in_drink', 'is_cover', 'is_addon', 'is_topping', 'is_big_ingredient', 'color'],
        booleanFields: ['available_in_food', 'available_in_drink', 'is_cover'],
        loadedKey: 'loaded',
        successAction: () => {
          this.data.units = this.data.units.map(unit => unit.unit_id);
          this.data.is_big_ingredient = !!this.data.is_big_ingredient;

          if (this.data.is_cover) {
            this.drinkIngredientType = 'cover';
          } else if (this.data.is_addon) {
            this.drinkIngredientType = 'addon';
          } else if (this.data.is_topping) {
            this.drinkIngredientType = 'topping';
          } else {
            this.drinkIngredientType = 'fluid';
          }

          if (!this.data.color) {
            this.data.color = '#ffffff';
          }

          this.loadImageToCheck();
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/system/units/items',
        params: {
          pagination: 0
        },
        outputKey: 'units',
        loadedKey: 'unitsLoaded'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/ingredients/items',
        params: {
          pagination: 0
        },
        outputKey: 'ingredients',
        loadedKey: 'ingredientsLoaded'
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['ingredient-form'], this.submitForm);
    },
    submitForm () {
      let endpointUrl = this.isEdit ? '/ingredients/update/' + this.id : '/ingredients/store';

      if (this.data.image !== null) {
        let formDataToSend = {
          available_in_food: +this.data.available_in_food,
          available_in_drink: +this.data.available_in_drink,
          is_cover: +this.data.is_cover,
          is_addon: +this.data.is_addon,
          is_topping: +this.data.is_topping,
          is_big_ingredient: +this.data.is_big_ingredient,
          color: typeof this.data.color === 'object' ? this.data.color.hex : this.data.color,
          name: this.data.name,
          preview_name: this.data.preview_name,
          type: this.data.type,
          units: this.data.units,
          image: this.data.image
        };

        FormUtils.submit(this, {
          endpoint: endpointUrl,
          formData: formDataToSend,
          successTitle: 'Zapisano składnik',
          successText: 'Wybrany składnik został zapisany',
          errorTitle: 'Wystąpił błąd',
          errorText: 'Zapis nie powiódł się. Spróbuj ponownie.',
          successAction: () => {
            this.$router.push('/ingredients')
          }
        }, ['image']);
      } else {
        let formDataToSend = {
          available_in_food: +this.data.available_in_food,
          available_in_drink: +this.data.available_in_drink,
          is_cover: +this.data.is_cover,
          is_addon: +this.data.is_addon,
          is_topping: +this.data.is_topping,
          is_big_ingredient: +this.data.is_big_ingredient,
          color: typeof this.data.color === 'object' ? this.data.color.hex : this.data.color,
          name: this.data.name,
          preview_name: this.data.preview_name,
          units: this.data.units,
          type: this.data.type
        };

        if ((!this.isComplexIngredient && this.svgCode) || (this.isComplexIngredient && this.wrappedSvgCode)) {
          formDataToSend.image_svg_code = this.pureSvgCode.replace(/class="is-inactive-layer-in-complex-editor"/gmi, '');
        }

        FormUtils.submit(this, {
          endpoint: endpointUrl,
          formData: formDataToSend,
          successTitle: 'Zapisano składnik',
          successText: 'Wybrany składnik został zapisany',
          errorTitle: 'Wystąpił błąd',
          errorText: 'Zapis nie powiódł się. Spróbuj ponownie.',
          successAction: () => {
            this.$router.push('/ingredients')
          }
        });
      }
    },
    setSvgImage (e) {
      let file = e.target.files[0];
      let reader = new FileReader();

      reader.addEventListener('load', (event) => {
        let loadedSvgCode = event.target.result;
        let loadedSvgDimensions = SvgHelper.getSvgSize(loadedSvgCode);

        if (!loadedSvgDimensions.width || !loadedSvgDimensions.height) {
          this.$swal({
            title: 'Błąd wczytywania grafiki SVG',
            html: 'Nie udało się odczytać rozmiaru wybranej grafiki SVG. Popraw grafikę lub spróbuj użyć innej grafiki SVG.',
            type: 'error',
            confirmButtonText: 'OK',
            buttonsStyling: true
          });
          return;
        }

        this.svgCode = loadedSvgCode;
        this.svgCode = SvgHelper.prepareSvgAsLayer(this.svgCode);
        let calculatedInitialPosition = SvgHelper.calculateInitialPosition(loadedSvgDimensions, {
          width: 800,
          height: this.data.is_big_ingredient ? 400 : 200
        });
        this.simpleEditorState.layerX = calculatedInitialPosition.x;
        this.simpleEditorState.layerY = calculatedInitialPosition.y;
        this.simpleEditorState.layerScale = calculatedInitialPosition.scale;
        this.simpleEditorState.layerScaleInPercents = this.simpleEditorState.layerScale * 100;
        this.simpleEditorState.imageWidth = loadedSvgDimensions.width;
        this.simpleEditorState.imageHeight = loadedSvgDimensions.height;

        nextTick(() => {
          this.$refs['editor-editable-layer'].style.left = this.simpleEditorState.layerX + 'px';
          this.$refs['editor-editable-layer'].style.top = this.simpleEditorState.layerY + 'px';
          this.updateEditorEditableLayerScale();
        });
      });
      reader.readAsText(file);
    },
    setAsSimpleIngredient () {
      if (this.complexEditorState.layers.length) {
        this.$swal({
          title: 'Obecna grafika zostanie usunięta',
          text: 'Zmiana typu składnika spowoduje wyczyszczenie obecnej grafiki',
          showCancelButton: true,
          confirmButtonText: 'OK, wyczyść',
          cancelButtonText: 'Anuluj',
          buttonsStyling: true
        }).then((result) => {
          if (result.value) {
            this.resetSimpleEditor();
            this.resetComplexEditor();
            this.isComplexIngredient = false;
          }
        });
      } else {
        this.isComplexIngredient = false;
      }
    },
    setAsComplexIngredient () {
      if (this.svgCode) {
        this.$swal({
          title: 'Obecna grafika zostanie usunięta',
          text: 'Zmiana typu składnika spowoduje wyczyszczenie obecnej grafiki',
          showCancelButton: true,
          confirmButtonText: 'OK, wyczyść',
          cancelButtonText: 'Anuluj',
          buttonsStyling: true
        }).then((result) => {
          if (result.value) {
            this.resetSimpleEditor();
            this.resetComplexEditor();
            this.isComplexIngredient = true;
          }
        });
      } else {
        this.isComplexIngredient = true;
      }
    },
    setAsFoodIngredient () {
      if (this.data.image) {
        this.$swal({
          title: 'Obecna grafika zostanie usunięta',
          text: 'Zmiana dostępności składnika spowoduje wyczyszczenie obecnej grafiki',
          showCancelButton: true,
          confirmButtonText: 'OK, wyczyść',
          cancelButtonText: 'Anuluj',
          buttonsStyling: true
        }).then((result) => {
          if (result.value) {
            this.data.image = null;
            this.data.available_in_food = true;
            this.data.available_in_drink = false;
          }
        });
      } else {
        this.data.available_in_food = true;
        this.data.available_in_drink = false;
      }
    },
    setAsDrinkIngredient () {
      if (this.svgCode) {
        this.$swal({
          title: 'Obecna grafika zostanie usunięta',
          text: 'Zmiana dostępności składnika spowoduje wyczyszczenie obecnej grafiki',
          showCancelButton: true,
          confirmButtonText: 'OK, wyczyść',
          cancelButtonText: 'Anuluj',
          buttonsStyling: true
        }).then((result) => {
          if (result.value) {
            this.resetSimpleEditor();
            this.resetComplexEditor();
            this.data.available_in_food = false;
            this.data.available_in_drink = true;
          }
        });
      } else {
        this.data.available_in_food = false;
        this.data.available_in_drink = true;
      }
    },
    resetSimpleEditor () {
      if (this.$refs['file-input']) {
        this.$refs['file-input'].clear();
      }

      this.svgCode = '';
      this.simpleEditorState.layerX = 0;
      this.simpleEditorState.layerY = 0;
      this.simpleEditorState.layerScale = 2.5;
      this.simpleEditorState.imageWidth = 0;
      this.simpleEditorState.imageHeight = 0;
      this.simpleEditorState.layerScaleInPercents = 250;
      this.simpleEditorState.moveEditableLayer = false;
    },
    resetComplexEditor () {
      this.complexEditorState.layers = [];
      this.complexEditorState.moveEditableLayer = false;
      this.complexEditorState.selectedLayer = false;
    },
    editorDecreaseLayerScale () {
      if (this.isComplexIngredient) {
        this.complexEditorState.layers[this.complexEditorSelectedLayerIndex].layerScaleInPercents = this.complexEditorState.layers[this.complexEditorSelectedLayerIndex].layerScaleInPercents - 10;
        this.complexEditorState.layers[this.complexEditorSelectedLayerIndex].layerScaleInPercents = Math.round(this.complexEditorState.layers[this.complexEditorSelectedLayerIndex].layerScaleInPercents);
        this.complexEditorState.layers[this.complexEditorSelectedLayerIndex].layerScale = this.complexEditorState.layers[this.complexEditorSelectedLayerIndex].layerScaleInPercents / 100;
      } else {
        this.simpleEditorState.layerScaleInPercents -= 10;
        this.simpleEditorState.layerScaleInPercents = Math.round(this.simpleEditorState.layerScaleInPercents);
        this.simpleEditorState.layerScale = this.simpleEditorState.layerScaleInPercents / 100;
      }

      this.updateEditorEditableLayerScale();
    },
    editorIncreaseLayerScale () {
      if (this.isComplexIngredient) {
        this.complexEditorState.layers[this.complexEditorSelectedLayerIndex].layerScaleInPercents = parseFloat(this.complexEditorState.layers[this.complexEditorSelectedLayerIndex].layerScaleInPercents) + 10;
        this.complexEditorState.layers[this.complexEditorSelectedLayerIndex].layerScaleInPercents = Math.round(this.complexEditorState.layers[this.complexEditorSelectedLayerIndex].layerScaleInPercents);
        this.complexEditorState.layers[this.complexEditorSelectedLayerIndex].layerScale = this.complexEditorState.layers[this.complexEditorSelectedLayerIndex].layerScaleInPercents / 100;
      } else {
        this.simpleEditorState.layerScaleInPercents = parseFloat(this.simpleEditorState.layerScaleInPercents) + 10;
        this.simpleEditorState.layerScaleInPercents = Math.round(this.simpleEditorState.layerScaleInPercents);
        this.simpleEditorState.layerScale = this.simpleEditorState.layerScaleInPercents / 100;
      }

      this.updateEditorEditableLayerScale();
    },
    editorUpdateLayerScale () {
      if (this.isComplexIngredient) {
        this.complexEditorState.layers[this.complexEditorSelectedLayerIndex].layerScale = this.complexEditorState.layers[this.complexEditorSelectedLayerIndex].layerScaleInPercents / 100;
      } else {
        this.simpleEditorState.layerScale = this.simpleEditorState.layerScaleInPercents / 100;
      }

      this.updateEditorEditableLayerScale();
    },
    updateEditorEditableLayerScale () {
      if (this.isComplexIngredient) {
        let svgHeight = this.data.is_big_ingredient ? 400 : 200;
        this.$refs['editor-editable-layer'].style.height = (svgHeight * this.complexEditorState.layers[this.complexEditorSelectedLayerIndex].layerScale) + 'px';
        this.$refs['editor-editable-layer'].style.width = (800 * this.complexEditorState.layers[this.complexEditorSelectedLayerIndex].layerScale) + 'px';
      } else {
        this.$refs['editor-editable-layer'].style.height = (this.simpleEditorState.imageHeight * this.simpleEditorState.layerScale) + 'px';
        this.$refs['editor-editable-layer'].style.width = (this.simpleEditorState.imageWidth * this.simpleEditorState.layerScale) + 'px';
      }
    },
    editorEditableLayerMouseDown (event) {
      let layer = this.$refs['editor-editable-layer'].getBoundingClientRect();

      if (this.isComplexIngredient) {
        this.complexEditorState.moveEditableLayer = true;
        this.complexEditorState.clickedPointX = event.clientX - layer.x;
        this.complexEditorState.clickedPointY = event.clientY - layer.y;
      } else {
        this.simpleEditorState.moveEditableLayer = true;
        this.simpleEditorState.clickedPointX = event.clientX - layer.x;
        this.simpleEditorState.clickedPointY = event.clientY - layer.y;
      }
    },
    editorEditableLayerMouseUp () {
      if (this.isComplexIngredient) {
        this.complexEditorState.moveEditableLayer = false;
        this.complexEditorState.clickedPointX = 0;
        this.complexEditorState.clickedPointY = 0;
      } else {
        this.simpleEditorState.moveEditableLayer = false;
        this.simpleEditorState.clickedPointX = 0;
        this.simpleEditorState.clickedPointY = 0;
      }
    },
    editorEditableLayerMouseMove (event) {
      if (this.isComplexIngredient && this.complexEditorState.moveEditableLayer) {
        let layerParent = this.$refs['editor-editable-layer'].parentNode.getBoundingClientRect();
        let cursorX = event.clientX - layerParent.x;
        let cursorY = event.clientY - layerParent.y;
        let positionX = cursorX - this.complexEditorState.clickedPointX;
        let positionY = cursorY - this.complexEditorState.clickedPointY;
        this.$refs['editor-editable-layer'].style.left = positionX + 'px';
        this.$refs['editor-editable-layer'].style.top = positionY + 'px';
        this.complexEditorState.layers[this.complexEditorSelectedLayerIndex].layerX = positionX;
        this.complexEditorState.layers[this.complexEditorSelectedLayerIndex].layerY = positionY;
      } else if (!this.isComplexIngredient && this.simpleEditorState.moveEditableLayer) {
        let layerParent = this.$refs['editor-editable-layer'].parentNode.getBoundingClientRect();
        let cursorX = event.clientX - layerParent.x;
        let cursorY = event.clientY - layerParent.y;
        let positionX = cursorX - this.simpleEditorState.clickedPointX;
        let positionY = cursorY - this.simpleEditorState.clickedPointY;
        this.$refs['editor-editable-layer'].style.left = positionX + 'px';
        this.$refs['editor-editable-layer'].style.top = positionY + 'px';
        this.simpleEditorState.layerX = positionX;
        this.simpleEditorState.layerY = positionY;
      }
    },
    addIngredientToComplexProduct () {
      this.complexEditorState.layers.push({
        id: +new Date(),
        layerX: 0,
        layerY: 0,
        layerScale: 1,
        svgCode: '',
        ingredientID: null,
        ingredientName: '',
        layerScaleInPercents: 100
      });
    },
    toggleLayerSelection (layerID) {
      if (this.complexEditorState.selectedLayer === layerID) {
        this.complexEditorState.selectedLayer = false;
      } else {
        this.complexEditorState.selectedLayer = layerID;
        nextTick(() => {
          this.$refs['editor-editable-layer'].style.left = this.complexEditorState.layers[this.complexEditorSelectedLayerIndex].layerX + 'px';
          this.$refs['editor-editable-layer'].style.top = this.complexEditorState.layers[this.complexEditorSelectedLayerIndex].layerY + 'px';
          this.updateEditorEditableLayerScale();
        });
      }
    },
    loadIngredient (index) {
      console.log('loadIngredient called for index:', index);
      let ingredientID = this.complexEditorState.layers[index].ingredientID;
      console.log('Ingredient ID:', ingredientID);
      let foundedIngredient = this.ingredients.filter(ingredient => ingredient.id === ingredientID);

      if (foundedIngredient[0]) {
        console.log('Found ingredient:', foundedIngredient[0]);
        let svgUrl = foundedIngredient[0].image_url;
        this.complexEditorState.layers[index].ingredientName = foundedIngredient[0].name;

        if (!svgUrl) {
          console.warn('Składnik nie ma przypisanej ilustracji SVG:', foundedIngredient[0].name);
          return;
        }

        console.log('Loading SVG from URL:', svgUrl);
        if (window.location.host === 'localhost:8080') {
          svgUrl = svgUrl.replace(AppConfig.apiServerUrl, 'https://localhost:8080');
        }

        this.$refs['svg-image-loader'].setAttribute('data', svgUrl);
        this.$refs['svg-image-loader'].setAttribute('data-index', index);
      } else {
        console.warn('Ingredient not found with ID:', ingredientID);
      }
    },
    removeLayer (layerID) {
      this.complexEditorState.layers = this.complexEditorState.layers.filter(layer => layer.id !== layerID);

      if (this.complexEditorState.selectedLayer === layerID) {
        this.toggleLayerSelection(layerID);
      }
    },
    svgImageLoaded () {
      console.log('SVG Image loaded event triggered');
      let loadedSvgCode = this.$refs['svg-image-loader'].contentDocument.documentElement.outerHTML;
      let layerIndex = parseInt(this.$refs['svg-image-loader'].getAttribute('data-index'), 10);
      console.log('Layer index:', layerIndex);
      console.log('Loaded SVG code:', loadedSvgCode.substring(0, 200));
      this.$refs['svg-image-loader'].removeAttribute('data-index');
      this.complexEditorState.layers[layerIndex].svgCode = SvgHelper.prepareSvgAsLayer(loadedSvgCode);
      console.log('Prepared SVG code:', this.complexEditorState.layers[layerIndex].svgCode.substring(0, 200));
      this.complexEditorState.layers[layerIndex].layerX = 0;
      this.complexEditorState.layers[layerIndex].layerY = 0;
      this.complexEditorState.layers[layerIndex].layerScale = 1;
      this.complexEditorState.layers[layerIndex].layerScaleInPercents = 100;
    },
    setImage (event) {
      this.data.image = event.target.files[0];
    },
    removeImage () {
      this.data.image_url = '';
      this.data.image = null;
    },
    loadImageToCheck () {
      let svgUrl = this.data.image_url;
      if (window.location.host === 'localhost:8080') {
        svgUrl = svgUrl.replace(AppConfig.apiServerUrl, 'https://localhost:8080');
      }

      this.$refs['svg-image-type-checker'].setAttribute('data', svgUrl);
    },
    svgImageToCheckLoaded () {
      let loadedSvgCode = this.$refs['svg-image-type-checker'].contentDocument.documentElement.outerHTML;

      if (loadedSvgCode.indexOf('data-is-complex-product="true"') > -1) {
        this.isComplexIngredient = true;
      } else {
        this.isComplexIngredient = false;
      }

      this.complexEditorState.layers = SvgHelper.parseComplexProduct(this.$refs['svg-image-type-checker'].contentDocument.documentElement);
    },
    setAsNormalIngredient () {
      this.data.is_big_ingredient = false;
    },
    setAsBigIngredient () {
      this.data.is_big_ingredient = true;
    },
    showDuplicateModal () {
      this.$swal({
        title: 'Potwierdzenie duplikacji',
        html: 'Czy na pewno chcesz zduplikować SKŁADNIK - <b>' + (this.data.name || '') + '</b>?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Duplikuj',
        cancelButtonText: 'Anuluj',
        buttonsStyling: true
      }).then((result) => {
        if (result.isConfirmed) {
          this.duplicateIngredient();
        }
      });
    },
    duplicateIngredient () {
      this.$http
        .post('/ingredients/duplicate/' + this.data.id)
        .then((response) => {
          if (response.data.success) {
            const newIngredientId = response.data.new_id || 
                                   response.data.id || 
                                   response.data.ingredient_id || 
                                   (response.data.item && response.data.item.id) ||
                                   (response.data.ingredient && response.data.ingredient.id);
            
            if (newIngredientId) {
              this.$swal({
                title: 'Sukces',
                text: response.data.message || 'Składnik został zduplikowany',
                icon: 'success',
              }).then(() => {
                this.$router.push('/ingredients/' + newIngredientId);
              });
            } else {
              this.$swal({
                title: 'Sukces',
                text: response.data.message || 'Składnik został zduplikowany',
                icon: 'success',
              });
            }
          } else {
            this.$swal({
              title: 'Błąd',
              text: response.data.message || 'Nie udało się zduplikować składnika',
              icon: 'error',
            });
          }
        })
        .catch((error) => {
          this.$swal({
            title: 'Błąd',
            text: 'Wystąpił problem podczas duplikowania składnika',
            icon: 'error',
          });
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.image-wrapper {
  background: #fff;
  border: 2px dashed #ccc;
  border-radius: 10px;
  height: 204px;
  margin: 30px auto;
  overflow: hidden;
  position: relative;
  width: 804px;

  img,
  svg {
    display: block;
    height: 200px;
    width: 800px;
  }

  &-code {
    height: 200px;
    width: 800px;
  }

  &.is-big {
    height: 404px;

    img,
    svg {
      height: 400px;
    }

    .image-wrapper-code {
      height: 400px;
    }
  }

  &-editable-layer {
    cursor: move;
    position: absolute;
    z-index: 1;
  }

  &-scale {
    margin: 0 auto;
    width: 240px;
  }

  .image-area-lines-x,
  .image-area-lines-y {
    height: 100%;
    left: 0;
    position: absolute;
    pointer-events: none;
    top: 0;
    width: 100%;
  }

  .image-area-line-y {
    border-top: 1px dashed #ccc;
    height: 1px;
    left: 0;
    position: absolute;
    width: 100%;

    &:last-of-type {
      display: none;
    }

    @for $i from 1 through 8 {
      &:nth-child(#{$i}) {
        top: calc(#{$i} * 50px);
      }
    }
  }

  .image-area-line-x {
    border-left: 1px dashed #ccc;
    height: 100%;
    left: 6.25%;
    position: absolute;
    width: 1px;

    @for $i from 2 through 15 {
      &:nth-child(#{$i}) {
        left: calc(#{$i} * 6.25%);
      }
    }
  }
}

.layers-list {
  list-style-type: none;
  padding: 0;
}

.layer {
  align-items: center;
  display: flex;
  padding: 10px 0;
  position: relative;

  .card {
    border-radius: 10px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, .125);
    width: 100%;
  }

  &-values {
    align-items: center;
    margin: 0;
    padding: 0;
    width: 100%;
  }

  &.is-selected {
    .card {
      background: #fff4de;
      border: 2px solid #eedda9;
      color: #856404;

      .layer-remove {
        display: flex;
      }
    }
  }

  &-remove {
    align-items: center;
    background: var(--danger);
    border-radius: 50%;
    display: none;
    height: 30px;
    justify-content: center;
    position: absolute;
    right: -15px;
    top: -15px;
    width: 30px;

    i {
      position: relative;
      top: 0px;
    }
  }
}

.complex-editor-sidebar {
  position: relative;
  z-index: 10;
}

// Radio buttons w pionie z marginesem z prawej
.form-group {
  ::v-deep .custom-radio,
  ::v-deep .custom-control {
    display: block !important;
    margin-bottom: 0.5rem !important;
    margin-right: 1rem !important;
    
    input[type="radio"] {
      margin-right: 5px !important;
    }
    
    label {
      margin-left: 5px !important;
    }
  }
}

// Color picker - pełna szerokość bez scrolla
.color-picker-wrapper {
  ::v-deep .vc-swatches {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 100% !important;
    min-height: 400px !important;
    height: auto !important;
    box-shadow: none !important;
    padding: 0 !important;
    overflow: visible !important;
    border: none !important;
  }

  ::v-deep .vc-swatches-box {
    padding: 0 !important;
    overflow: visible !important;
    overflow-y: visible !important;
    overflow-x: visible !important;
    max-height: none !important;
    min-height: 400px !important;
    height: auto !important;
  }

  ::v-deep .vc-swatches-pick {
    overflow: visible !important;
    overflow-y: visible !important;
    max-height: none !important;
    min-height: 400px !important;
    height: auto !important;
  }
}
</style>

<style lang="scss">
/* Global styles for vue-color without scoped */
.vc-swatches,
.vc-swatches-picker {
  width: auto !important;
  max-width: auto !important;
  height: auto !important;
  max-height: none !important;
  box-shadow: none !important;
  webkit-box-shadow: none !important;
  padding: 0 !important;
  overflow: visible !important;
  overflow-y: visible !important;
  border: none !important;
}

.vc-swatches-box {
  padding: 0 !important;
  overflow: visible !important;
  overflow-y: visible !important;
  overflow-x: visible !important;
  max-height: none !important;
  height: auto !important;
}

.vc-swatches-pick {
  overflow: visible !important;
  overflow-y: visible !important;
  max-height: none !important;
  height: auto !important;
}
</style>
